import { useEffect, useState, useRef, useCallback } from 'react';
import { currentAuthenticatedUser } from '../services/auth/authService';

// WebSocket URL from environment or aws-exports
const getWebSocketUrl = () => {
  // Try environment variable first
  if (import.meta.env.VITE_WEBSOCKET_URL) {
    return import.meta.env.VITE_WEBSOCKET_URL;
  }

  // Fallback: construct from aws-exports if needed
  // This would need to be manually configured in .env
  console.warn('VITE_WEBSOCKET_URL not set in environment');
  return null;
};

// Reconnect strategy - exponential backoff
const RECONNECT_DELAYS = [1000, 2000, 5000, 10000, 30000];
const MAX_RECONNECT_ATTEMPTS = 5;

// Heartbeat (ping/pong) to keep connection alive
const HEARTBEAT_INTERVAL = 30000; // 30 seconds
const HEARTBEAT_TIMEOUT = 5000; // 5 seconds to wait for pong

/**
 * WebSocket client hook for real-time event updates
 *
 * Features:
 * - Automatic connection with Cognito JWT auth
 * - Auto-reconnect with exponential backoff
 * - Heartbeat to keep connection alive
 * - Connection status tracking
 * - Message queuing when disconnected
 *
 * @param {Object} options
 * @param {boolean} options.enabled - Whether to connect (default: true)
 * @param {Function} options.onMessage - Callback for incoming messages
 * @param {Function} options.onError - Callback for errors
 * @param {Function} options.onConnect - Callback when connected
 * @param {Function} options.onDisconnect - Callback when disconnected
 *
 * @returns {Object} { connected, connectionState, send, close, reconnect, error }
 */
export function useWebSocket(options = {}) {
  const {
    enabled = true,
    onMessage,
    onError,
    onConnect,
    onDisconnect
  } = options;

  const [connected, setConnected] = useState(false);
  const [connectionState, setConnectionState] = useState('disconnected');
  // States: 'disconnected', 'connecting', 'connected', 'reconnecting', 'error'
  const [reconnectAttempt, setReconnectAttempt] = useState(0);
  const [error, setError] = useState(null);

  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const heartbeatTimerRef = useRef(null);
  const heartbeatTimeoutRef = useRef(null);
  const messageQueueRef = useRef([]);
  const mountedRef = useRef(true);
  const enabledRef = useRef(enabled);

  // Keep enabled ref in sync
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  /**
   * Send heartbeat ping to keep connection alive
   */
  const sendHeartbeat = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      console.log('[WebSocket] Sending heartbeat ping');
      wsRef.current.send(JSON.stringify({ action: 'ping' }));

      // Set timeout to detect missed pong
      heartbeatTimeoutRef.current = setTimeout(() => {
        console.warn('[WebSocket] Heartbeat timeout - no pong received');
        // Force reconnect
        if (wsRef.current) {
          wsRef.current.close();
        }
      }, HEARTBEAT_TIMEOUT);
    }
  }, []);

  /**
   * Start heartbeat interval
   */
  const startHeartbeat = useCallback(() => {
    stopHeartbeat();
    heartbeatTimerRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);
    console.log('[WebSocket] Heartbeat started');
  }, [sendHeartbeat]);

  /**
   * Stop heartbeat interval
   */
  const stopHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
    if (heartbeatTimeoutRef.current) {
      clearTimeout(heartbeatTimeoutRef.current);
      heartbeatTimeoutRef.current = null;
    }
  }, []);

  /**
   * Connect to WebSocket with JWT token
   */
  const connect = useCallback(async () => {
    if (!enabledRef.current || !mountedRef.current) return;

    const wsUrl = getWebSocketUrl();
    if (!wsUrl) {
      console.error('[WebSocket] No WebSocket URL configured');
      setError(new Error('WebSocket URL not configured'));
      setConnectionState('error');
      return;
    }

    try {
      setConnectionState('connecting');
      setError(null);

      // Get current Cognito session and ID token
      console.log('[WebSocket] Getting authenticated user...');
      const user = await currentAuthenticatedUser();
      console.log('[WebSocket] User retrieved:', {
        username: user?.username,
        hasSession: !!user?.signInUserSession,
        hasIdToken: !!user?.signInUserSession?.idToken,
        hasJwtToken: !!user?.signInUserSession?.idToken?.jwtToken
      });

      // Validate user session structure
      if (!user || !user.signInUserSession || !user.signInUserSession.idToken || !user.signInUserSession.idToken.jwtToken) {
        throw new Error('User session not found or invalid. Please sign in again.');
      }

      const idToken = user.signInUserSession.idToken.jwtToken;

      // Build WebSocket URL with token
      const url = `${wsUrl}?token=${idToken}`;

      console.log('[WebSocket] Connecting to:', wsUrl);

      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) return;

        console.log('[WebSocket] Connected');
        setConnected(true);
        setConnectionState('connected');
        setReconnectAttempt(0);
        setError(null);

        // Start heartbeat
        startHeartbeat();

        // Send queued messages
        while (messageQueueRef.current.length > 0) {
          const msg = messageQueueRef.current.shift();
          ws.send(msg);
        }

        if (onConnect) onConnect();
      };

      ws.onmessage = (event) => {
        if (!mountedRef.current) return;

        try {
          const data = JSON.parse(event.data);
          console.log('[WebSocket] Message received:', data);

          // Handle auth_required - session expired, force logout
          if (data.action === 'auth_required') {
            console.error('[WebSocket] Session expired - auth required');
            setConnectionState('auth_expired');
            setError(new Error(data.message || 'Session expired'));

            // Close connection
            if (wsRef.current) {
              wsRef.current.close(1000, 'Session expired');
            }

            // Trigger auth expired callback
            if (options.onAuthExpired) {
              options.onAuthExpired(data);
            }
            return;
          }

          // Handle pong response
          if (data.action === 'pong') {
            console.log('[WebSocket] Pong received');
            if (heartbeatTimeoutRef.current) {
              clearTimeout(heartbeatTimeoutRef.current);
              heartbeatTimeoutRef.current = null;
            }
            return;
          }

          // Pass message to callback
          if (onMessage) onMessage(data);
        } catch (err) {
          console.error('[WebSocket] Error parsing message:', err);
          if (onError) onError(err);
        }
      };

      ws.onerror = (event) => {
        if (!mountedRef.current) return;

        console.error('[WebSocket] Error:', event);
        const err = new Error('WebSocket connection error');
        setError(err);
        if (onError) onError(err);
      };

      ws.onclose = (event) => {
        if (!mountedRef.current) return;

        console.log('[WebSocket] Closed:', event.code, event.reason);
        setConnected(false);
        stopHeartbeat();

        if (onDisconnect) onDisconnect();

        // Attempt reconnect if enabled and not a normal closure
        if (enabled && event.code !== 1000) {
          console.log(`🔄 [WebSocket] Connection closed abnormally (code: ${event.code}), attempting reconnect...`);
          attemptReconnect();
        } else {
          console.log('✅ [WebSocket] Connection closed normally (code: 1000), not reconnecting');
          setConnectionState('disconnected');
        }
      };

    } catch (err) {
      console.error('[WebSocket] Connection error:', err);
      setError(err);
      setConnectionState('error');
      if (onError) onError(err);

      // Attempt reconnect on error
      if (enabled) {
        console.log('🔄 [WebSocket] Connection failed, attempting reconnect...');
        attemptReconnect();
      }
    }
  }, [enabled, onMessage, onError, onConnect, onDisconnect, startHeartbeat, stopHeartbeat]);

  /**
   * Attempt to reconnect with exponential backoff
   */
  const attemptReconnect = useCallback(() => {
    if (!enabledRef.current || !mountedRef.current) {
      console.log('[WebSocket] Reconnect skipped (enabled:', enabledRef.current, 'mounted:', mountedRef.current, ')');
      return;
    }

    if (reconnectAttempt >= MAX_RECONNECT_ATTEMPTS) {
      console.error('❌ [WebSocket] Max reconnect attempts reached (5/5) - giving up');
      setConnectionState('error');
      setError(new Error('Failed to reconnect after maximum attempts'));
      return;
    }

    const delay = RECONNECT_DELAYS[Math.min(reconnectAttempt, RECONNECT_DELAYS.length - 1)];
    console.log(`⏱️  [WebSocket] Scheduling reconnect in ${delay}ms (attempt ${reconnectAttempt + 1}/${MAX_RECONNECT_ATTEMPTS})`);

    setConnectionState('reconnecting');
    setReconnectAttempt(prev => prev + 1);

    reconnectTimerRef.current = setTimeout(() => {
      if (mountedRef.current && enabledRef.current) {
        console.log(`🔄 [WebSocket] Executing reconnect attempt ${reconnectAttempt + 1}/${MAX_RECONNECT_ATTEMPTS}...`);
        connect();
      } else {
        console.log('[WebSocket] Reconnect cancelled (component unmounted or disabled)');
      }
    }, delay);
  }, [enabled, reconnectAttempt, connect]);

  /**
   * Send message through WebSocket
   */
  const send = useCallback((data) => {
    const message = typeof data === 'string' ? data : JSON.stringify(data);

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(message);
    } else {
      console.warn('[WebSocket] Not connected, queueing message');
      messageQueueRef.current.push(message);
    }
  }, []);

  /**
   * Close WebSocket connection
   */
  const close = useCallback(() => {
    stopHeartbeat();

    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close(1000, 'Client closing');
      wsRef.current = null;
    }

    setConnected(false);
    setConnectionState('disconnected');
  }, [stopHeartbeat]);

  /**
   * Manually trigger reconnect
   */
  const reconnect = useCallback(() => {
    close();
    setReconnectAttempt(0);
    connect();
  }, [close, connect]);

  // Connect on mount if enabled
  useEffect(() => {
    if (enabled) {
      connect();
    }

    return () => {
      mountedRef.current = false;
      close();
    };
  }, [enabled, connect, close]);

  return {
    connected,
    connectionState,
    reconnectAttempt,
    error,
    send,
    close,
    reconnect
  };
}
